import React from "react"
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {endOfDay, formatISO, startOfYesterday} from "date-fns";

import Page from "../Page";
import MDBox from "../../materialDashboard/components/MDBox";
import MDButton from "../../materialDashboard/components/MDButton";
import GeoJSONMap from "../../components/maps/GeoJSONMap";
import GeoJSONPointsCollection from "../../components/maps/GeoJSONMap/GeoJSONPointsCollection";
import useDataTableFilters from "../../components/DataTable/useDataTableFilters";
import TableDataLoader from "./Models23Table/TableDataLoader";

import Card from "@mui/material/Card";
import {LayersControl} from "react-leaflet";
import {useCurrentUser} from "../../auth/CurrentUserProvider";
import {useAuth} from "../../auth/AuthProvider";
import useModels23Map from "./useModels23Map";
import renderStationTooltip from "../../components/maps/stationFeature/renderStationFeatureTooltip";
import renderModel23Tooltip from "../../components/maps/model23Feature/renderModel23FeatureTooltip";
import Box from "@mui/material/Box";
import StationFeatureChip from "../../components/maps/stationFeature/StationFeatureChip";
import {useGeoJSONModels23} from "../../components/maps/query";
import useSelectedFeatures from "../../components/maps/GeoJSONMap/useSelectedFeatures";
import Model23FeatureChip from "../../components/maps/model23Feature/Model23FeatureChip";
import {ExpandButtonIcon, useExpandButtonIcon} from "../../components/maps/ExpandButtonIcon";


const Models23 = () => {
    const {t} = useTranslation(['models23']);
    const navigate = useNavigate();
    const {currentUser} = useCurrentUser();
    const {workgroupAuthorization} = useAuth();

    // data states
    const todayStartISO = formatISO(startOfYesterday());
    const todayEndISO = formatISO(endOfDay(new Date));
    const selectedWorkgroups = currentUser.data.role === "ADMIN" ? [] : currentUser.data.workgroups;
    const filterProps = {
        initialValues: {
            datetimeGte: todayStartISO,
            datetimeLte: todayEndISO,
            selectedWorkgroups,
            stationAclUserId: currentUser.data.id,
            stationAclModel23Write: currentUser.data?.role === "WG_USER", // if user is a workgroup user, he can only see his own models by default
        }
    };
    const initialFiltersValue = {
        datetime__gte: todayStartISO,
        datetime__lte: todayEndISO,
        station__workgroup_id__in: selectedWorkgroups.map(w => w.id),
        station__acl__model23_write: currentUser.data?.role === "WG_USER",  // if user is a workgroup user, he can only see his own models by defaul
    }

    const filterState = useDataTableFilters({initialFiltersValue});

    // Station maps states
    const map = useModels23Map({models23Filters: filterState.filters})
    const expandButtonState = useExpandButtonIcon();

    // TODO wrap this login into a hook or add to the useModels23Map
    const geoModel23ItinerantQuery = useGeoJSONModels23({...filterState.filters, station__type: "ITINERANT"});
    const model23ItinerantGeoJSON = geoModel23ItinerantQuery.data ?? {type: "FeatureCollection", features: []};
    const model23ItinerantSelectedFeatures = useSelectedFeatures({features: model23ItinerantGeoJSON.features});

    return <Page>
        <MDBox px={2}>
            <Box justifyContent="space-between" alignItems="center" display="flex" flexWrap="wrap" gap={2} mb={2}>
                {
                    workgroupAuthorization.models23.create &&
                    <MDButton variant="gradient" color="info" onClick={() => navigate("/models23/new")}>
                        {t('newModel23')}
                    </MDButton>
                }
                <ExpandButtonIcon {...expandButtonState} />
            </Box>
            <MDBox ref={map.mapBoxRef}
                   borderRadius="xl"
                   bgColor="grey-300"
                   sx={{height: expandButtonState.mapHeight, scrollMarginTop: 100, overflow: "hidden", mt: 2, boxShadow: 1, position: "relative", zIndex: 2}}>
                <GeoJSONMap mapRef={map.mapRef}
                            data={[]} >
                    <LayersControl position="topright">
                        <LayersControl.Overlay name="stations" checked>
                            <GeoJSONPointsCollection featureCollection={map.geoJSON}
                                                     getSelectedFeatures={map.getSelectedFeatures}
                                                     onFeatureClick={map.handleFeatureClick}
                                                     cluster
                                                     renderFeatureTooltip={renderStationTooltip}
                            />
                        </LayersControl.Overlay>
                        <LayersControl.Overlay name="modelli23 itineranti" checked>
                            <GeoJSONPointsCollection featureCollection={model23ItinerantGeoJSON}
                                                     getSelectedFeatures={model23ItinerantSelectedFeatures.getSelectedFeatures}
                                                     onFeatureClick={model23ItinerantSelectedFeatures.handleFeatureClick}
                                                     cluster
                                                     renderFeatureTooltip={renderModel23Tooltip}
                            />
                        </LayersControl.Overlay>
                    </LayersControl>
                </GeoJSONMap>
            </MDBox>
            <Card sx={{pt: 3, mt: -3, zIndex: 1}}>
                {
                    map.selectedFeatures.length > 0 && <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, pt: 3, px: 3}}>
                        {
                            map.selectedFeatures.map(f =>
                                <StationFeatureChip key={f.properties.id}
                                                    feature={f}
                                                    onDelete={() => map.toggleFeature(f)}/>
                            )
                        }
                    </Box>
                }
                {
                    model23ItinerantSelectedFeatures.selectedFeatures.length > 0 && <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, pt: 3, px: 3}}>
                        {
                            model23ItinerantSelectedFeatures.selectedFeatures.map(f =>
                                <Model23FeatureChip key={f.properties.id}
                                                    feature={f}
                                                    onDelete={() => model23ItinerantSelectedFeatures.toggleFeature(f)}/>
                            )
                        }
                    </Box>
                }
                <TableDataLoader filterState={filterState}
                                 filterProps={filterProps}
                                 map={map}
                                 dataTableId="models23"
                                 id__in={model23ItinerantSelectedFeatures.selectedFeatureIds.join(",")}/>
            </Card>
        </MDBox>

    </Page>
}



export default Models23;